import React, { useEffect, useRef } from 'react';
import './ExportedStack.css';

// The bottom bar's exported zone: one card per video, in a horizontal stack that overlaps like a hand of cards.
//   cards = [{ id, src, state: 'exporting' | 'exported' | 'partial' | 'failed', badge, label, title, onClick, retryLabel, onRetry }]
//   badge = how many videos that clip holds (round, top-right, in the card's colour)
// A card lands the moment its export starts (fly-in) and the stack scrolls to the latest. Every card has ONE reference size (--xs-card-w,
// strictly 9:16). Hovering a card brings it to the front and scales it up (transform + z-index only, never width/height);
// the others stay behind, slightly smaller; leaving the stack restores the arrangement. Green = every variant came out; amber = partial ("2 of 3", Retry failed only);
// red = nothing came out (Retry). A card never turns green on less than everything and never vanishes.
export default function ExportedStack({ cards }) {
  const scroller = useRef(null);
  const seen = useRef(new Set());
  const fresh = new Set(cards.filter(c => !seen.current.has(c.id)).map(c => c.id));

  // Wheel over the cards scrolls the stack sideways (its own scrollbar is hidden so the cards can grow beyond the bar).
  const onWheel = (ev) => { const s = scroller.current; if (s && Math.abs(ev.deltaY) > Math.abs(ev.deltaX)) s.scrollLeft += ev.deltaY; };

  useEffect(() => {
    cards.forEach(c => seen.current.add(c.id));
    const s = scroller.current; if (!s) return undefined;
    const t = setTimeout(() => { s.scrollLeft = s.scrollWidth; }, 50);     // show what just came out
    return () => clearTimeout(t);
  }, [cards.length]); // eslint-disable-line

  return (
    <div className="xs-stack" ref={scroller} onWheel={onWheel}>
      <div className="xs-strip">
        {cards.map(c => (
          <div key={c.id} className={`xs-card xs-card--${c.state}${fresh.has(c.id) ? ' xs-new' : ''}`} title={c.title || undefined}
            onClick={c.onClick || undefined} role="button" tabIndex={0}>
            {c.badge != null && <span className="xs-badge">{c.badge}</span>}
            <div className="xs-face">
              {c.src && <video src={c.src} muted playsInline preload="metadata" />}
              <span className="xs-strip-color" />
              {c.label && <span className="xs-label">{c.label}</span>}
              {(c.state === 'failed' || c.state === 'partial') && (
                <button type="button" className="xs-retry" onClick={e => { e.stopPropagation(); c.onRetry && c.onRetry(); }}>{c.retryLabel || 'Retry'}</button>
              )}
              {c.state === 'exported' && <span className="xs-hover">re-edit</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
